import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL;

export const CatalogueDialog = ({ open, onOpenChange, category }) => {
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [downloadUrl, setDownloadUrl] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const reset = () => {
    setName(""); setCompany(""); setEmail(""); setPhone("");
    setStatus("idle"); setDownloadUrl(""); setErrorMsg("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    setErrorMsg("");
    try {
      const res = await fetch(`${API}/api/catalogue-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          company: company.trim() || null,
          email: email.trim(),
          phone: phone.trim() || null,
          category_id: category.id,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Could not generate catalogue. Please try again.");
      }
      const data = await res.json();
      const url = `${API}${data.download_url}`;
      setDownloadUrl(url);
      setStatus("success");
      // Auto-open the PDF in a new tab
      setTimeout(() => window.open(url, "_blank", "noopener"), 300);
    } catch (err) {
      setErrorMsg(err.message || "Something went wrong.");
      setStatus("error");
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) setTimeout(reset, 200);
      }}
    >
      <DialogContent
        className="bg-[color:var(--paper)] border border-[color:var(--line)] sm:max-w-md rounded-none p-0"
        data-testid="catalogue-dialog"
      >
        <div style={{ padding: "2rem 2rem 2.2rem" }}>
          <DialogHeader>
            <p
              className="mono"
              style={{
                color: "var(--brass-deep)",
                margin: 0,
                fontSize: ".68rem",
                letterSpacing: ".14em",
              }}
            >
              Asia Sourcing · Catalogue
            </p>
            <DialogTitle
              className="display"
              style={{
                fontSize: "1.6rem",
                fontWeight: 400,
                lineHeight: 1.15,
                margin: ".4rem 0 0",
                letterSpacing: "-.01em",
                color: "var(--ink)",
              }}
            >
              {status === "success"
                ? "Catalogue ready."
                : `Download the ${category.name} catalogue.`}
            </DialogTitle>
            <DialogDescription
              style={{
                color: "var(--ink-soft)",
                fontSize: ".92rem",
                lineHeight: 1.55,
                margin: ".7rem 0 0",
              }}
            >
              {status === "success"
                ? "We've opened the PDF in a new tab. A copy will also reach our Moradabad desk."
                : "A 4-page PDF with India's edge, testing protocols, MOQs and lead times. We'll keep your details on file for follow-up only."}
            </DialogDescription>
          </DialogHeader>

          {status === "success" ? (
            <div style={{ marginTop: "1.8rem", display: "flex", flexDirection: "column", gap: ".8rem" }}>
              <a
                href={downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-fill btn-lg"
                data-testid="catalogue-download-link"
              >
                <span>Download PDF again →</span>
              </a>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="btn btn-ghost"
                data-testid="catalogue-dialog-close"
              >
                Close
              </button>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              style={{ marginTop: "1.6rem", display: "flex", flexDirection: "column", gap: ".9rem" }}
              data-testid="catalogue-form"
            >
              <Field label="Full name *" value={name} onChange={setName} required testid="catalogue-input-name" />
              <Field label="Company" value={company} onChange={setCompany} testid="catalogue-input-company" />
              <Field label="Email *" type="email" value={email} onChange={setEmail} required testid="catalogue-input-email" />
              <Field label="Phone (optional)" value={phone} onChange={setPhone} testid="catalogue-input-phone" />

              {errorMsg && (
                <p
                  className="mono"
                  style={{ color: "#a44", fontSize: ".75rem", margin: 0 }}
                  data-testid="catalogue-error"
                >
                  {errorMsg}
                </p>
              )}

              <button
                type="submit"
                disabled={status === "submitting" || !name || !email}
                className="btn btn-fill btn-lg"
                style={{ marginTop: ".4rem" }}
                data-testid="catalogue-submit"
              >
                <span>
                  {status === "submitting" ? "Generating PDF…" : "Send me the catalogue →"}
                </span>
              </button>
              <p
                className="mono"
                style={{
                  color: "var(--ink-soft)",
                  fontSize: ".62rem",
                  letterSpacing: ".06em",
                  margin: ".4rem 0 0",
                  lineHeight: 1.6,
                }}
              >
                By submitting you agree to receive a follow-up email from Asia Sourcing. No newsletter spam.
              </p>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, value, onChange, type = "text", required, testid }) => (
  <label style={{ display: "flex", flexDirection: "column", gap: ".3rem" }}>
    <span
      className="mono"
      style={{
        color: "var(--brass-deep)",
        fontSize: ".62rem",
        letterSpacing: ".12em",
        textTransform: "uppercase",
      }}
    >
      {label}
    </span>
    <input
      type={type}
      value={value}
      required={required}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      style={{
        background: "var(--paper-raised)",
        border: "1px solid var(--line)",
        padding: ".7rem .85rem",
        fontFamily: "var(--display)",
        fontSize: ".95rem",
        color: "var(--ink)",
        borderRadius: 2,
        outline: "none",
      }}
    />
  </label>
);

export default CatalogueDialog;
